import api from '../api';
import ApiConstant from '../../constants/api.constants';
import orderServices from '../orders/orderservice';



import {
    // ProductDTO,
    UserDTO,
    UserAddressDTO,
    CourierProviderDTO
} from '../../models/sales-ordering/sales-ordering-model';
import { ProductNewModel } from '../../models/products/product-new-model';

class SalesOrderingService {

    async getMostOrderedProducts(viewAll: boolean = false): Promise<ProductNewModel[]> {
        try {
            const response = await api.post(ApiConstant.MostOrderedProductDetails + `?Isallproduct=${viewAll}`, {});
            const data = response.data;
            const mapped = this.mapSalesDTOToProductNewModel(data);
            return viewAll ? mapped : mapped.slice(0, 6);
        } catch (error) {
            return [];
        }
    }

    async searchProducts(query: string = ''): Promise<ProductNewModel[]> {
        try {
            if (!query) return [];
            const response = await api.post(ApiConstant.SearchProduct + `?Productname=${query}`, {});
            const data = response.data;
            return this.mapSalesDTOToProductNewModel(data);
        } catch (error) {
            return [];
        }
    }

    async searchProductsByCode(code: string): Promise<ProductNewModel[]> {
        try {
            if (!code || !code.trim()) return [];
            const response = await api.get(`${ApiConstant.SearchByCode}?code=${encodeURIComponent(code.trim())}`);
            const data = response.data;
            return this.mapSalesDTOToProductNewModel(data);
        } catch (error) {
            return [];
        }
    }

    private getImageUrl(path: string): string {
        if (!path) return '';
        if (path.startsWith('http') || path.startsWith('data:image')) return path;

        const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT || '';
        const cleanBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;

        return cleanBase + cleanPath;
    }

    private mapSalesDTOToProductNewModel(data: any[]): ProductNewModel[] {
        if (Array.isArray(data)) {
            return data.map((p: any) => ({
                id: p.id || p.productId || p.ProductId,
                name: p.name || p.productName || p.Name,
                description: p.description || p.Description || p.name || p.productName || '',
                unitPrice: p.unitPrice || p.price || p.UnitPrice || 0,
                tax: p.tax || 0,
                totalUnitPrice: (() => { const up = p.unitPrice || p.price || p.UnitPrice || 0; const gst = p.tax || p.gst || p.GST || 0; return Math.round(up + (up * gst / 100)); })(),
                weight: p.weight || p.unit || '',
                minOrderQty: p.minOrderQty || 1,
                orderMultiples: p.orderMultiples || 1,
                productImageList: (() => {
                    // Case 1: productImageList is an array (from ProductDataAccess/home-page API)
                    if (Array.isArray(p.productImageList) && p.productImageList.length > 0) {
                        const img = p.productImageList.find((img: any) => img.isDefault || img.IsDefault) || p.productImageList[0];
                        return { id: img.id || 0, isDefault: img.isDefault ?? true, mediaUrl: img.mediaUrl || img.MediaUrl || '' };
                    }
                    // Case 2: productImageList is already a single UploadedImage object
                    if (p.productImageList && typeof p.productImageList === 'object' && p.productImageList.mediaUrl) {
                        return p.productImageList;
                    }
                    // Case 3: media array (old format)
                    if (p.media && Array.isArray(p.media) && p.media.length > 0) {
                        return { id: 0, isDefault: true, mediaUrl: p.media[0]?.url || p.media[0]?.mediaUrl || '' };
                    }
                    // Case 4: flat filePath string (from SalesDataAccess)
                    const fp = p.filePath || p.FilePath || p.productImage || p.ProductImage;
                    if (fp) return { id: 0, isDefault: true, mediaUrl: this.getImageUrl(fp) };
                    return { id: 0, isDefault: true, mediaUrl: '' };
                })(),
                categoryID: p.categoryID || p.categoryId || 0,
                categoryName: p.categoryName || '',
                subCategoryID: p.subCategoryID || p.subCategoryId || 0,
                subCategoryName: p.subCategoryName || '',
                courierAvailable: p.courierAvailable !== undefined ? p.courierAvailable : (p.CourierAvailable !== undefined ? p.CourierAvailable : (p.isCourierAvailable || false)),
                inStock: p.inStock !== undefined && p.inStock !== null ? p.inStock : (p.InStock !== undefined && p.InStock !== null ? p.InStock : (p.stock !== undefined && p.stock !== null ? p.stock : 100)),
                isHomeDeliveryAvailable: p.isHomeDeliveryAvailable !== undefined ? p.isHomeDeliveryAvailable : (p.IsHomeDeliveryAvailable !== undefined ? p.IsHomeDeliveryAvailable : (p.isHomeOnly || false)),
                ForCustomer: p.ForCustomer !== undefined ? p.ForCustomer : (p.forCustomer !== undefined ? p.forCustomer : true)
            }));
        }
        return [];
    }

    async searchUsers(query: string = ''): Promise<UserDTO[]> {
        try {
            // Using logic similar to UserDropDownSelect
            const result = await orderServices.getuserdetails(query);

            if (Array.isArray(result)) {
                return result.map((u: any) => ({
                    userId: u.userID || u.userId,
                    name: u.name,
                    mobile: u.primaryContactNbr || u.mobileNumber || u.mobile,
                    addresses: []
                }));
            }
            return [];
        } catch (error) {
            return [];
        }
    }

    async getUserAddresses(userId: number): Promise<UserAddressDTO[]> {
        try {
            // Use the same API as the User Address page to get full address details
            const UserAddressService = (await import('../useraddress/useraddress.service')).default;
            const response = await UserAddressService.getUserAddressById(userId);

            if (response && Array.isArray(response)) {
                return response
                    .filter((a: any) => !a.isDeleted)
                    .map((a: any) => ({
                        addressId: a.userAddressID || a.addressId,
                        name: a.userName,
                        mobile: a.mobileNumber || a.mobile,
                        addressLine1: a.address1 || '',
                        addressLine2: [a.address2, a.landmark, a.areaName, a.subDisticName].filter(Boolean).join(', '),
                        city: a.cityName || '',
                        state: a.stateName || '',
                        pincode: a.zipcode || '',
                        addressType: a.addressType,
                        homeDeliveryAvailable: a.homeDeliveryAvailable
                    }));
            }
            return [];
        } catch (error) {
            return [];
        }
    }

    async getCourierProviders(stateName?: string, totalWeight: number = 0): Promise<CourierProviderDTO[]> {
        try {
            const response = await api.get(ApiConstant.getAllCourierProviders);

            if (response && response.data) {
                // Import CriteriaChecker to determine weight slab
                const { CriteriaChecker } = await import('../../components/courier-provider/courier-criteria');
                const weightCriteria = CriteriaChecker(totalWeight);

                // Filter by state and weight criteria like the existing implementation
                let filteredData = response.data;

                if (stateName) {
                    filteredData = filteredData.filter((c: any) =>
                        c.stateName === stateName && c.criteria === weightCriteria
                    );
                } else {
                    // If no state provided, just filter by criteria
                    filteredData = filteredData.filter((c: any) => c.criteria === weightCriteria);
                }


                // Deduplicate by courierProviderID - keep only unique providers
                const uniqueProviders = new Map();
                filteredData.forEach((c: any) => {
                    const providerId = c.courierProviderID || c.courierId || c.id;
                    // Only add if we haven't seen this provider ID yet
                    if (!uniqueProviders.has(providerId)) {
                        uniqueProviders.set(providerId, c);
                    }
                });

                const deduplicatedData = Array.from(uniqueProviders.values());

                const adjustedWeight = Math.max(Math.ceil(totalWeight), 1);

                const mappedData = deduplicatedData.map((c: any) => {
                    const ratePerKg = c.amount || c.charges || 0;
                    const calculatedCharges = ratePerKg * adjustedWeight + ratePerKg;
                    return {
                        providerId: c.courierProviderID || c.courierId || c.id,
                        providerName: c.providerName || c.courierName,
                        charges: calculatedCharges,
                        logoUrl: ''
                    };
                });

                return mappedData;
            }
            return [];
        } catch (error) {
            return [];
        }
    }


    async createCustomer(customerData: { name: string; mobile: string; createdBy: number }): Promise<any> {
        try {
            const response = await api.post(ApiConstant.createCustomer, {
                name: customerData.name,
                mobile: customerData.mobile,
                createdBy: customerData.createdBy
            });

            if (response && response.data) {
                return response.data;
            }

            return {
                success: false,
                message: 'Failed to create customer'
            };
        } catch (error: any) {
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error creating customer'
            };
        }
    }

    async getPaymentMethods(): Promise<any[]> {
        try {
            const response = await api.get(ApiConstant.Getpaymentmethods);
            if (response && response.data) {
                return response.data;
            }
            return [];
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            return [];
        }
    }

    async createOrderBySales(orderData: any): Promise<any> {
        try {
            const response = await api.post(ApiConstant.CreateOrderBySales, orderData);
            if (response && response.data) {
                return response.data;
            }
            return { success: false, message: 'Failed to create order' };
        } catch (error: any) {
            // Axios errors contain the request body, including device coordinates.
            console.error("Error creating order by sales");
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error creating order'
            };
        }
    }

    async createSalesOrder(order: any): Promise<any> {
        try {
            return new Promise(resolve => setTimeout(() => resolve({ success: true, orderId: 12345 }), 1000));
        } catch (error) {
            throw error;
        }
    }

    async getBatchesByPosAndProduct(posId: number | null, productId: number, keyword: string = ""): Promise<any[]> {
        try {
            const response = await api.post(
                ApiConstant.GetSalesBatchesByPosAndProduct,
                { posId, productId, keyword },
                { headers: { "Content-Type": "application/json" } }
            );
            return response.data || [];
        } catch (error) {
            console.error("Error fetching sales batches:", error);
            return [];
        }
    }
}

export default new SalesOrderingService();
